import logger from 'utils/logger';

/**
 * Identification readiness. Flag reads fall back to `safeFlagDefaults` until LaunchDarkly has
 * initialized and every requested `identify` has settled, so callers whose behavior is not
 * reversible once a default is observed (an irreversible submission, for example) need to know
 * when a flag value can be trusted. The count starts at 1 for initialization.
 *
 * This module holds no client and triggers no initialization so that reading readiness never
 * pulls LaunchDarkly startup into a consumer.
 */
let pendingIdentifyCount = 1;
const listeners = new Set<() => void>();

const notifyListeners = () => {
  listeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      logger.error({ error, message: 'Error notifying LaunchDarkly identify listener' });
    }
  });
};

export const beginLaunchDarklyIdentify = () => {
  pendingIdentifyCount += 1;
  notifyListeners();
};

export const settleLaunchDarklyIdentify = () => {
  pendingIdentifyCount = Math.max(pendingIdentifyCount - 1, 0);
  notifyListeners();
};

/** True once initialization and all requested identify calls have settled (resolved or failed). */
export const isLaunchDarklyIdentified = () => pendingIdentifyCount === 0;

export const subscribeToLaunchDarklyIdentify = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
